/**
 * Per-studio policy snapshot the SPA pins at login — the sibling of
 * `studio-tz`. Currently just the cancellation window
 * (`cancellation_deadline_hours`), which the calendar reads to decide whether a
 * booked-slot drag lands INSIDE the late-change window (→ the credit-penalty
 * prompt).
 *
 * `DashboardLayout` calls {@link setCancellationDeadlineHours} with the
 * `/api/whoami` `studio.cancellation_deadline_hours` BEFORE any page renders,
 * mirroring `setStudioTimeZone`. Deliberately NOT reactive: it changes only via
 * the `/config` editor save, which reloads the app so open pages re-pin.
 *
 * Default 0 = no window (nothing counts as late) until whoami lands — the SAFE
 * default: a drag behaves exactly as it did before this feature.
 */

let currentCancellationDeadlineHours = 0;

/**
 * Pin the session's cancellation window (hours). A non-finite / negative value
 * (or `null` from an unwired loader) resets to 0 — no drag is treated as late.
 */
export function setCancellationDeadlineHours(hours: number | null): void {
  currentCancellationDeadlineHours =
    typeof hours === "number" && Number.isFinite(hours) && hours > 0 ? hours : 0;
}

/** The session's cancellation window in hours (0 = no window). */
export function cancellationDeadlineHours(): number {
  return currentCancellationDeadlineHours;
}

/**
 * True when `startsAtIso` falls inside the studio's cancellation window from
 * now — i.e. a change to it is a "late" change (credit penalty applies). False
 * when the window is 0/unset, or the session is already past / outside it.
 */
export function isWithinCancellationWindow(startsAtIso: string): boolean {
  const hours = currentCancellationDeadlineHours;
  if (hours <= 0) return false;
  const startMs = Date.parse(startsAtIso);
  if (Number.isNaN(startMs)) return false;
  const msUntil = startMs - Date.now();
  // Late = the session is still upcoming (msUntil > 0) but within the window.
  return msUntil > 0 && msUntil < hours * 3_600_000;
}
